type Range = { low: number; high: number } | null;
const pounds = (value: number) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value);
const modelNotice =
  'This is an illustrative calculation from fixed model assumptions, not observed deal prices, a market benchmark or a recommended quote. Production costs, usage rights, exclusivity and tax are not priced separately by this model.';
const nextStep =
  'Before quoting: add deliverables, production time and costs, revisions, dates, usage scope and duration, exclusivity and tax. Use your own cost worksheet alongside this scenario.';

export type FollowerPricingModel = {
  basePerThousand: number;
  engagementDivisor: number;
  formatMultiplier: number;
  lowerFactor: number;
  upperFactor: number;
};

export function followerPricingExport(input: {
  platform: 'instagram' | 'tiktok';
  followers: number;
  engagementRate: number;
  format: string;
  result: Range;
  model: FollowerPricingModel;
}): string | null {
  if (
    !input.result ||
    ![input.followers, input.engagementRate, input.result.low, input.result.high].every(
      Number.isFinite,
    )
  )
    return null;
  const { platform, followers, engagementRate, format, result, model } = input;
  return [
    `${platform === 'instagram' ? 'Instagram' : 'TikTok'} illustrative sponsorship model`,
    `Format: one ${format.toLowerCase()}`,
    `Illustrative range: ${pounds(result.low)} to ${pounds(result.high)}`,
    `Followers entered: ${followers}`,
    `Follower-based engagement rate entered: ${engagementRate}%`,
    'Engagement input definition: (average likes + average comments) ÷ followers × 100. This is not the views-based engagement rate shown in OwlScran media kits.',
    '',
    `Base calculation: ${followers} ÷ 1,000 × £${model.basePerThousand} × (${engagementRate} ÷ ${model.engagementDivisor}) × ${model.formatMultiplier}.`,
    `Model assumptions: £${model.basePerThousand} per 1,000 followers; engagement divisor ${model.engagementDivisor}; format multiplier ${model.formatMultiplier}.`,
    `Range: base × ${model.lowerFactor} to base × ${model.upperFactor}, rounded to whole pounds.`,
    modelNotice,
    '',
    nextStep,
    'Inputs were entered manually and are not verified against a connected account. Add your source and reporting window before sharing.',
    `Calculator: https://www.owlscran.com/${platform}-influencer-pricing-calculator`,
  ].join('\n');
}

export function viewConsistencyMultiplier(subscribers: number, views: number): number {
  const ratio = views / subscribers;
  return ratio >= 0.25 ? 1.1 : ratio < 0.05 ? 0.9 : 1;
}

export function youtubePricingExport(input: {
  subscribers: number;
  avgViews: number;
  format: string;
  result: Range;
  cpm: { low: number; high: number };
  minimum: { low: number; high: number };
}): string | null {
  if (
    !input.result ||
    ![input.subscribers, input.avgViews, input.result.low, input.result.high].every(Number.isFinite)
  )
    return null;
  const { subscribers, avgViews, format, result, cpm, minimum } = input;
  const multiplier = viewConsistencyMultiplier(subscribers, avgViews);
  return [
    'YouTube illustrative sponsorship model',
    `Format: one ${format.toLowerCase()}`,
    `Illustrative range: ${pounds(result.low)} to ${pounds(result.high)}`,
    `Subscribers entered: ${subscribers}`,
    `Average views per long-form video entered: ${avgViews}`,
    '',
    `Model price per 1,000 views: £${cpm.low} to £${cpm.high}. Minimum fees: £${minimum.low} and £${minimum.high}.`,
    `Views ÷ subscribers: ${avgViews} ÷ ${subscribers} = ${avgViews / subscribers}. Applied multiplier: ${multiplier}.`,
    'Model multiplier rule: 1.1 at a views/subscribers ratio of 0.25 or more; 0.9 below 0.05; otherwise 1. These are fixed assumptions, not measured relationships.',
    `Low: max(£${minimum.low}, ${avgViews} ÷ 1,000 × £${cpm.low} × ${multiplier}).`,
    `High: max(£${minimum.high}, ${avgViews} ÷ 1,000 × £${cpm.high} × ${multiplier}).`,
    'Both endpoints are rounded to the nearest £10. This is a sponsorship scenario, not YouTube advertising revenue.',
    modelNotice,
    '',
    nextStep,
    'Inputs were entered manually and are not verified against a connected account. Add your source and reporting window before sharing.',
    'Calculator: https://www.owlscran.com/youtube-influencer-pricing-calculator',
  ].join('\n');
}
